import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon, MobileHeader, EmptyState } from '@/components';
import { useToast } from '@/components';
import { useCourse, useEnrollments, useSubmitEvaluation } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';
import { evalQuestionsFor, LIKERT_LABELS } from '@/data/evaluation';
import type { LikertValue } from '@/types/domain';

const SCALE: LikertValue[] = [1, 2, 3, 4, 5];

/**
 * Course evaluation — a 5-point Likert template that satisfies the `eval`
 * certificate requirement. Reached from the course detail when the course
 * requires an evaluation.
 */
export function LearnerEval() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { L, locale } = useLocalized();
  const { toast } = useToast();
  const { data: course } = useCourse(id);
  const submit = useSubmitEvaluation();
  const { data: enrollments = [] } = useEnrollments();
  const [answers, setAnswers] = useState<Record<string, LikertValue>>({});
  const [comment, setComment] = useState('');

  const questions = evalQuestionsFor(id);

  if (!course) return <EmptyState icon="star" title="Evaluation not found" />;

  // Survey is one-time — once submitted, don't allow redoing it.
  const enr = enrollments.find((e) => e.courseId === id);
  if (enr?.eval) {
    return (
      <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
        <MobileHeader title="Evaluation" th="แบบประเมิน" onBack={() => navigate(-1)} large={false} />
        <div style={{ maxWidth: 480, margin: '0 auto', padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center', minHeight: '60vh', justifyContent: 'center' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--sage-soft)', display: 'grid', placeItems: 'center', color: 'var(--sage)' }}>
            <Icon name="check" size={40} stroke={2.2} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Survey complete</h2>
          <p className="th" style={{ fontSize: 14.5, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0, maxWidth: 320 }}>
            ขอบคุณที่ทำแบบประเมินแล้ว · Thanks — you've already completed the survey.
          </p>
          <button className="btn btn-primary btn-lg btn-block" style={{ maxWidth: 280 }} onClick={() => navigate('/learn/wallet')}><Icon name="award" size={17} /> View certificate</button>
        </div>
      </div>
    );
  }

  const complete = questions.every((q) => answers[q.id] != null);

  const onSubmit = () => {
    submit.mutate(
      { courseId: id, answers, comment: comment.trim() || undefined },
      {
        onSuccess: () => {
          toast('Evaluation submitted — thank you', { icon: 'star' });
          // Survey is the final step → the certificate just auto-issued; show it.
          navigate('/learn/wallet');
        },
      },
    );
  };

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader title="Evaluation" th="แบบประเมิน" onBack={() => navigate(-1)} large={false} />
      <div className="hidden md:block" style={{ padding: '18px 28px 0' }}>
        <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm"><Icon name="chevL" size={15} /> Back</button>
      </div>
      <div style={{ maxWidth: 620, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22, padding: '18px 20px 28px' }}>
        <div className="card" style={{ padding: 16 }}>
          <div className="th" style={{ fontSize: 16, fontWeight: 600 }}>{L(course.title)}</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>{course.title.en} · {course.host}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, fontSize: 13, color: 'var(--ink-2)' }}>
            <Icon name="star" size={16} style={{ color: 'var(--sage)', flex: 'none', marginTop: 1 }} />
            <span>Your feedback completes the evaluation requirement. Responses are anonymous to the instructor.</span>
          </div>
        </div>

        {questions.map((q, i) => (
          <div key={q.id} className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <span className="u-mono" style={{ fontSize: 12, color: 'var(--ink-4)' }}>{String(i + 1).padStart(2, '0')}</span>
              <div>
                <div style={{ fontSize: 14.5, fontWeight: 500, lineHeight: 1.4 }}>{q.prompt.en}</div>
                <div className="th" style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 1 }}>{q.prompt.th}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 7, marginTop: 14 }}>
              {SCALE.map((v) => {
                const on = answers[q.id] === v;
                return (
                  <button
                    key={v}
                    onClick={() => setAnswers((p) => ({ ...p, [q.id]: v }))}
                    title={locale === 'th' ? LIKERT_LABELS[v].th : LIKERT_LABELS[v].en}
                    style={{
                      flex: 1,
                      border: `1.5px solid ${on ? 'var(--sage)' : 'var(--line-2)'}`,
                      background: on ? 'var(--sage)' : 'var(--card)',
                      color: on ? 'var(--on-accent)' : 'var(--ink-2)',
                      borderRadius: 10,
                      padding: '12px 0',
                      cursor: 'pointer',
                      fontFamily: 'var(--mono)',
                      fontSize: 15,
                      fontWeight: 600,
                      transition: 'all .14s',
                    }}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10.5, color: 'var(--ink-4)', fontFamily: 'var(--mono)', letterSpacing: '.03em' }}>
              <span>{locale === 'th' ? LIKERT_LABELS[1].th : 'Disagree'}</span>
              <span>{locale === 'th' ? LIKERT_LABELS[5].th : 'Agree'}</span>
            </div>
          </div>
        ))}

        <label className="field">
          <span className="label">Additional suggestions · ข้อเสนอแนะเพิ่มเติม (optional)</span>
          <textarea className="textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="ข้อเสนอแนะเพิ่มเติม…" />
        </label>

        <button className="btn btn-primary btn-lg btn-block" disabled={!complete || submit.isPending} onClick={onSubmit}>
          {submit.isPending ? 'Submitting…' : 'Submit evaluation'} <Icon name="arrowR" size={17} />
        </button>
        {!complete && <p style={{ margin: 0, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>Answer all {questions.length} questions to submit.</p>}
      </div>
    </div>
  );
}
